import { api } from '@hit-app/convex';
import { useAction, useQuery } from 'convex/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { useWorkOSAuth } from '../lib/auth';
import { reportPostHogError } from '../lib/posthog';
import { NoOrganization } from './NoOrganization';
import { FullScreenLoader } from './ui/feedback';

/**
 * Route layout that requires a Convex member record in the active WorkOS organization.
 * Creates the member profile automatically on first login; otherwise shows the NoOrganization screen.
 * Renders `children` when given, else the nested routes.
 */
export function OrganizationGate({ children }: { children?: ReactNode }) {
  const createOnLogin = useAction(api.workos.ensureCurrentMemberProfile);
  const { organizationId, user } = useWorkOSAuth();
  const userEmail = user?.email;
  const membership = useQuery(api.members.checkMembership, {});
  const creationKey = JSON.stringify([organizationId, userEmail]);
  const [failure, setFailure] = useState<{ key: string; message: string } | null>(null);
  const createError = failure?.key === creationKey ? failure.message : null;
  const needsProfile = membership?.hasOrg === false && membership.reason === 'no_membership';
  const creation = useRef<{ key: string; promise: Promise<unknown> } | null>(null);
  const isCreating = needsProfile && Boolean(userEmail) && !createError;

  useEffect(() => {
    if (!needsProfile || !userEmail) return;
    const key = creationKey;
    if (creation.current?.key !== key) {
      creation.current = { key, promise: createOnLogin({}) };
    }
    let active = true;
    void creation.current.promise.catch((error: unknown) => {
      if (!active) return;
      reportPostHogError(error, { area: 'membership', action: 'auto_create_profile', route: '/organization-gate' });
      setFailure({ key, message: 'Fehler beim Erstellen des Profils. Bitte versuchen Sie es erneut.' });
    });
    return () => {
      active = false;
    };
  }, [needsProfile, userEmail, createOnLogin, creationKey]);

  if (membership === undefined || isCreating) {
    return <FullScreenLoader label={isCreating ? 'Profil wird erstellt...' : 'Laden...'} />;
  }

  if (createError) {
    return (
      <div className="min-h-screen bg-app-gradient flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center" role="alert">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Fehler</h2>
          <p className="text-gray-600 mb-4">{createError}</p>
          <button type="button" onClick={() => window.location.reload()} className="btn-primary">
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  if (!membership.hasOrg) {
    return <NoOrganization userEmail={userEmail} reason={membership.reason} />;
  }

  return children ?? <Outlet />;
}
